import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

/**
 * Onramp Quote - Fee breakdown for deposit
 */
export interface OnrampQuote {
  totalAmount: string;
  subtotal: string;
  usdcAmount: string;
  coinbaseFee: string;
  networkFee: string;
  currency: string;
}

/**
 * Onramp Transaction - Historical deposit record
 */
export interface OnrampTransaction {
  status: string;
  purchaseCurrency: string;
  purchaseNetwork: string;
  purchaseAmount: string;
  paymentTotal: string;
  paymentSubtotal: string;
  coinbaseFee: string;
  networkFee: string;
  txHash?: string;
  transactionId: string;
  walletAddress: string;
  createdAt: string;
}

/**
 * Coinbase Onramp API
 * Handles USDC deposits via Coinbase Pay (card, Apple Pay, Coinbase account)
 */
export const onrampApi = {
  /**
   * Generate onramp URL with quote
   * Opens Coinbase Pay for user to complete deposit
   *
   * @param amount - USD amount to deposit (5-500 for guest checkout)
   * @param country - Optional ISO 3166-1 country code (defaults to US)
   * @param subdivision - Optional ISO 3166-2 subdivision code
   */
  createUrl: async (
    amount: number,
    country?: string,
    subdivision?: string
  ): Promise<{ onrampUrl: string; quote: OnrampQuote; quoteId: string }> => {
    const response = await apiClient.post(API_ENDPOINTS.ONRAMP.CREATE_URL, {
      amount,
      country,
      subdivision,
    });
    // Extract from response wrapper
    return {
      onrampUrl: response.data.onrampUrl,
      quote: response.data.quote,
      quoteId: response.data.quoteId,
    };
  },

  /**
   * Get user's onramp transaction history
   *
   * @param limit - Number of transactions to return (default: 10)
   */
  getTransactions: async (limit: number = 10): Promise<OnrampTransaction[]> => {
    const response = await apiClient.get(API_ENDPOINTS.ONRAMP.TRANSACTIONS, {
      params: { limit },
    });
    return response.data.transactions || [];
  },

  /**
   * Check if onramp service is available
   * Returns false if CDP API credentials are not configured
   */
  getStatus: async (): Promise<{ available: boolean; message: string }> => {
    const response = await apiClient.get(API_ENDPOINTS.ONRAMP.STATUS);
    return response.data;
  },

  /**
   * Get pending transaction status
   * Used for polling after initiating deposit to detect success/failure
   */
  getPendingStatus: async (): Promise<{ transaction: any | null }> => {
    const response = await apiClient.get(API_ENDPOINTS.ONRAMP.PENDING_STATUS);
    return { transaction: response.data.transaction };
  },
};
